import { z } from "zod";

/* ===========================================================================
 * US-E18.F2-0195 (@UC9275–@UC9278) — ĐA HÌNH THÁI QUỸ (value form) + ĐỐI SOÁT CHÉO KÊNH.
 *
 * 3 TRỤC ĐỘC LẬP — KHÔNG TRỘN:
 *   - `kind`      = phân loại KẾ TOÁN (CASH | INCOME | EXPENSE | MEMBER_RECEIVABLE | ...).
 *   - `currency`  = ĐƠN VỊ TIỀN (mirror cứng fund→account→line: CHECK/FK/trigger — KHÔNG đụng; FX = F3).
 *   - `valueForm` = PHƯƠNG THỨC NẮM GIỮ (CASH tiền mặt | BANK tài khoản NH | EWALLET ví điện tử) — TRỤC MỚI.
 *     Thuần METADATA hình thái: KHÔNG đổi ngữ nghĩa kế toán, KHÔNG đổi currency.
 *
 * GET  /v1/funds/:id/value-forms       — số dư CASH tách theo kênh + drift từng kênh (nền F6).
 * POST /v1/funds/:id/value-form-switch — đổi hình thái (vd rút mặt từ bank): 1 tx, 2 chân CASH
 *                                        (CREDIT kênh nguồn / DEBIT kênh đích) ⇒ 🔴 Σ CASH BẤT BIẾN.
 *
 * B1 tiền BigInt: mọi số tiền là STRING minor (^-?\d+$). KHÔNG float, KHÔNG number.
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;
const POSITIVE_INTEGER_STRING_PATTERN = /^\d+$/;
const PG_BIGINT_MAX = 9223372036854775807n;
const AMOUNT_MAX_DIGITS = 19;
const MEMO_MAX = 500;
const IDEMPOTENCY_KEY_MAX = 200;

/** Hình thái nắm giữ giá trị. Whitelist = nguồn sự thật DB CHECK `ledger_accounts_value_form_check`. */
export const VALUE_FORM = {
  CASH: "CASH",
  BANK: "BANK",
  EWALLET: "EWALLET",
} as const;

export type ValueForm = (typeof VALUE_FORM)[keyof typeof VALUE_FORM];

export const valueFormSchema = z.enum([VALUE_FORM.CASH, VALUE_FORM.BANK, VALUE_FORM.EWALLET]);

/** Mã lỗi 422 khi 2 kênh trùng nhau (đổi hình thái từ BANK → BANK = vô nghĩa). */
export const VALUE_FORM_SAME_CODE = "VALUE_FORM_SAME" as const;
/** Mã lỗi 422 khi số dư kênh nguồn không đủ (fail-closed — không cho kênh âm). */
export const VALUE_FORM_INSUFFICIENT_CODE = "VALUE_FORM_INSUFFICIENT" as const;

export const VALUE_FORM_AMOUNT_INVALID_MESSAGE =
  "Số tiền đổi hình thái phải là số nguyên dương (đơn vị nhỏ nhất)";

const amountMinorString = z
  .string()
  .trim()
  .min(1, VALUE_FORM_AMOUNT_INVALID_MESSAGE)
  .max(AMOUNT_MAX_DIGITS, VALUE_FORM_AMOUNT_INVALID_MESSAGE)
  .regex(POSITIVE_INTEGER_STRING_PATTERN, VALUE_FORM_AMOUNT_INVALID_MESSAGE)
  .refine((v) => {
    try {
      const n = BigInt(v);
      return n > 0n && n <= PG_BIGINT_MAX;
    } catch {
      return false;
    }
  }, VALUE_FORM_AMOUNT_INVALID_MESSAGE);

/**
 * Body POST /v1/funds/:id/value-form-switch — rút tiền mặt từ bank (BANK→CASH), nạp ví (CASH→EWALLET)…
 * `.strict()` chặn over-post. Tiền = STRING minor (B1). Đơn-currency (currency của SỔ — client không
 * gửi currency: đổi kênh KHÔNG đụng currency-mirror).
 */
export const valueFormSwitchRequestSchema = z
  .object({
    fromValueForm: valueFormSchema,
    toValueForm: valueFormSchema,
    amountMinor: amountMinorString,
    /** Bắt buộc — double-submit ⇒ đúng 1 tx (B4 idempotency). */
    idempotencyKey: z.string().trim().min(1).max(IDEMPOTENCY_KEY_MAX),
    memo: z.string().trim().max(MEMO_MAX).optional(),
  })
  .strict();

export type ValueFormSwitchRequest = z.infer<typeof valueFormSwitchRequestSchema>;

/** Kết quả đổi hình thái: 1 tx + số dư 2 kênh SAU đổi + Σ CASH (BẤT BIẾN — client verify được). */
export const valueFormSwitchResponseSchema = z
  .object({
    txId: z.string().uuid(),
    fundId: z.string().uuid(),
    fromValueForm: valueFormSchema,
    toValueForm: valueFormSchema,
    amountMinor: z.string().regex(POSITIVE_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    fromBalanceMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    toBalanceMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Σ CASH toàn sổ SAU đổi — PHẢI bằng trước đổi (tiền chỉ đổi KÊNH, không sinh/mất). */
    totalMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    switchedAt: z.string().datetime(),
  })
  .strict();

export type ValueFormSwitchResponse = z.infer<typeof valueFormSwitchResponseSchema>;

/**
 * 1 kênh trong báo cáo đối soát chéo kênh.
 * - `balanceMinor`  = cache `account_balances` (O(1) — nguồn hiển thị).
 * - `ledgerMinor`   = Σ ledger_lines THẬT của kênh (nguồn sự thật).
 * - `driftMinor`    = balanceMinor − ledgerMinor (≠ 0 ⇒ cache lệch ĐÚNG kênh đó — nền F6).
 */
export const valueFormChannelSchema = z
  .object({
    valueForm: valueFormSchema,
    balanceMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    ledgerMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    driftMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();

export type ValueFormChannel = z.infer<typeof valueFormChannelSchema>;

/**
 * GET /v1/funds/:id/value-forms — breakdown số dư CASH theo kênh.
 * 🔴 BẤT BIẾN: Σ(channels[].balanceMinor) = totalMinor (không kênh nào rơi ngoài).
 */
export const valueFormBreakdownResponseSchema = z
  .object({
    fundId: z.string().uuid(),
    currency: z.string().length(3),
    /** LUÔN đủ 3 kênh (kênh chưa dùng ⇒ 0) — client không phải đoán kênh vắng. */
    channels: z.array(valueFormChannelSchema).length(3),
    totalMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** true ⇔ có ÍT NHẤT 1 kênh drift ≠ 0 (fail-loud cho đối soát — F6 hiển thị cờ đỏ). */
    hasDrift: z.boolean(),
  })
  .strict();

export type ValueFormBreakdownResponse = z.infer<typeof valueFormBreakdownResponseSchema>;
